import type { ComponentsDefinition } from '@eneco/cms';

import Accordion from './Accordion';
import AddressFinder from './AddressFinder';
import AddressFinderWithMovingOption from './AddressFinderWithMovingOption';
import Article from './Article';
import BreadCrumbs from './BreadCrumbs';
import Bucket from './Bucket';
import ButtonLineUp from './ButtonLineUp';
import ButtonLink from './ButtonLink';
import CardGrid from './CardGrid';
import Carousel from './Carousel';
import Centered from './Centered';
import ChatEntryPoint from './ChatEntryPoint';
import ChatLinks from './ChatLinks';
import Checklist from './Checklist';
import ContactCustomerService from './ContactCustomerService';
import ContactForm from './ContactForm';
import ContactMethods from './ContactMethods';
import ContentCard from './ContentCard';
import ContentCardRebranded from './ContentCardRebranded';
import ContentCategories from './ContentCategories';
import ContractConfirmation from './ContractConfirmation';
import CreditWarningBar from './CreditWarningBar';
import CtaBlock from './CtaBlock';
import CustomerRating from './CustomerRating';
import CustomerReview from './CustomerReview';
import DeceasedForm from './DeceasedForm';
import Divider from './Divider';
import DownloadContracts from './DownloadContracts';
import { DynamicPriceCardElectricityV2, DynamicPriceCardGasV2 } from './DynamicPriceCard';
import FaqAccordion from './FaqAccordion';
import FeatureGrid from './FeatureGrid';
import FeaturePreview from './FeaturePreview';
import FlourishChart from './FlourishChart';
import Footer from './Footer';
import FooterMenu from './FooterMenu';
import { formFieldComponents } from './generic-form/fields';
import GenericForm from './GenericForm';
import GTM from './GTM';
import HeatDamageForm from './HeatDamageForm';
import Hero from './Hero';
import HeroCard from './HeroCard';
import HeroCardRebranded from './HeroCardRebranded';
import IconList from './IconList';
import Image from './Image';
import LanguageSelector from './LanguageSelector';
import LineUp from './LineUp';
import LinkCard from './LinkCard';
import List from './List';
import Logo from './Logo';
import MainNavigation from './MainNavigation';
import MegaMenu from './MegaMenu';
import MegaMenuDivision from './MegaMenuDivision';
import MegaMenuV2 from './MegaMenuV2';
import MessageBar from './MessageBar';
import ModelContractTable from './ModelContractTable';
import MyEnvDisabledLandingPageCard from './MyEnvDisabledLandingPageCard';
import NavigationView from './NavigationView';
import NewsletterFormIntegratedWithRevolution from './NewsletterFormIntegratedWithRevolution';
import NotificationBox from './NotificationBox';
import NotificationsBar from './NotificationsBar';
import PaginatedContent from './PaginatedContent';
import PersonalizationCookieController from './PersonalizationCookieController';
import PersonalizedTitleTextCTA from './PersonalizedTitleTextCTA';
import PersonalizedVideo from './PersonalizedVideo';
import Price from './Price';
import QuickLinks from './QuickLinks';
import RegisterToNewsletterForm from './RegisterToNewsletterForm';
import RevolutionRegisterToNewsletterForm from './RevolutionRegisterToNewsletterForm';
import RichText from './RichText';
import Section from './Section';
import ShareOnSocials from './ShareOnSocials';
import SideNav from './SideNav';
import SimpleTable from './SimpleTable';
import SmeAddressFinder from './SmeAddressFinder';
import SocialMediaMenu from './SocialMediaMenu';
import SplitView from './SplitView';
import StepExplainer from './StepExplainer';
import StickyNav from './StickyNav';
import StoreButtons from './StoreButtons';
import SubFooterMenu from './SubFooterMenu';
import Table from './Table';
import TabNavigation from './TabNavigation';
import Testimonials from './Testimonials';
import TextImageRebranded from './TextImageRebranded';
import TextStreamer from './TextStreamer';
import TitleTextCTA from './TitleTextCTA';
import TopTaskCTA from './TopTaskCTA';
import TopTasks from './TopTasks';
import UsabillaFeedback from './UsabillaFeedback';
import USPItem from './USPItem';
import Video from './Video';
import WarmthCheckAddressFinder from './WarmthCheckAddressFinder';
import WarmthInterruptions from './WarmthInterruptions';

/**
 * CMS-agnostic component registry (Phase E), keyed by
 * `CmsRendering.componentName` — which is the Sanity block `_type` on the Sanity
 * side and the JSS `componentName` on the Sitecore side, both verbatim. A block
 * with no entry renders a visible `[missing component: <type>]` marker in dev and
 * logs `cMsPh1`.
 *
 * ONE registry serves BOTH providers on purpose: `?_cms=sitecore` and
 * `?_cms=sanity` render the same route through this same map, so any visual
 * difference is a content-pipeline difference rather than a rendering one.
 *
 * KEY CASING: the migration bundle's section types are PascalCase and equal the
 * Sitecore rendering names 1:1 (verified against `component-map.json` and
 * `sitecoreComponents.ts`), so one key covers both lanes — true for `Divider`,
 * `Image`, `RichText`, `Section`, `SplitView`, `TitleTextCTA`, `HeroCard`,
 * `LineUp`, `ContentCard`, `MainNavigation`, `Logo`, `MegaMenuV2`, `Footer`, and
 * `SubFooterMenu`. `hero` (lowercase) is the exception — it is the earlier
 * greenfield POC schema's type name, kept alongside `Hero` so a dataset on
 * either generation resolves. `featureGrid`/`cardGrid`/`testimonials`/
 * `faqAccordion`/`ctaBlock` are POC-schema-only too (lowercase, live in dev, no
 * migration-bundle equivalent) — see each component's own `*-content.types.ts`
 * for why they're hand-typed rather than generated.
 *
 * `Section`/`SplitView`/`TitleTextCTA`/`HeroCard`/`LineUp`/`ContentCard`/
 * `MainNavigation`/`Footer` render nested placeholders (`sectionMain`, `jss-logo`,
 * `jss-subfooter-left`, …) resolved via `slots.ts`'s `resolveSlot`. Real migrated
 * content needs none of those nested names configured anywhere: it's one flat list
 * per page, and `libs/cms-sanity/src/adapter.ts`'s `toPage` reconstructs the tree
 * from each block's own `parentKey`/`area` fields (the placeholder name, literally
 * `jss-*`) — see that function's doc comment. `toRendering` additionally recognizes
 * genuine field-level nesting from its *content* shape alone (no `_ref`, `_type`
 * isn't `'block'`), for schemas that don't use the flat convention. Only the page
 * document's own top-level content field (typically `sections`) still needs
 * `FE_SANITY_SLOT_FIELDS`/`SANITY_SLOT_FIELDS` (`libs/cms-sanity/src/provider-
 * adapter.ts`), since GROQ needs that one name to select the field at all.
 *
 * `Logo`/`MegaMenuV2`/`SubFooterMenu` carry no fields of their own on real content:
 * their content is a `siteWideContent` reference (ADR-0063) to a document shared
 * across every page on the site, which `queries.ts` follows on every block and the
 * Sanity adapter also offers as the block's own fields. See each component's own
 * `*-content.types.ts` for what's on the other end.
 *
 * `TopTasks` and `TopTaskCTA` are two genuinely different, independently-existing
 * legacy components (`libs/sitecore/components/content/src/TopTasks/` and `.../
 * TopTaskCTA/`), not one split in two by the migration — `TopTasks` renders a
 * `TileBar` from its own multilist field, `TopTaskCTA` a single `Card` from its own
 * fields. Both `_type`s are independently allowed as flat siblings directly in
 * `Hero`'s `jss-hero-bottom` area (`libs/cms-sanity-schema/src/lib/containerSlots.ts`),
 * so both are registered.
 */
const cmsComponents: ComponentsDefinition = {
  hero: Hero,
  Hero,
  AddressFinder,
  AddressFinderWithMovingOption,
  SmeAddressFinder,
  CustomerReview,
  FeaturePreview,
  ContactCustomerService,
  ContactForm,
  ContactMethods,
  ChatLinks,
  Checklist,
  ContentCategories,
  FlourishChart,
  IconList,
  MessageBar,
  PaginatedContent,
  Price,
  QuickLinks,
  SideNav,
  StickyNav,
  TopTasks,
  UsabillaFeedback,
  TopTaskCTA,
  Divider,
  Image,
  RichText,
  featureGrid: FeatureGrid,
  cardGrid: CardGrid,
  testimonials: Testimonials,
  faqAccordion: FaqAccordion,
  ctaBlock: CtaBlock,
  Section,
  SplitView,
  TitleTextCTA,
  PersonalizedTitleTextCTA,
  Video,
  HeroCard,
  LineUp,
  LinkCard,
  ContentCard,
  MainNavigation,
  Logo,
  Accordion,
  // The GB home page is on the rebranded design-system variants; /about is on the originals, so both
  // generations are registered side by side until the content settles on one.
  Article,
  Carousel,
  Centered,
  NavigationView,
  List,
  ContentCardRebranded,
  HeroCardRebranded,
  TextImageRebranded,
  TextStreamer,
  USPItem,
  // `HeroRebranded` declares exactly the fields `Hero` does (image + the same six appearance
  // parameters), and the legacy bridge's own doc calls it "identical wiring to `Hero`" — the two
  // differ only in the inner visual treatment, which this lane does not reproduce for any block.
  // Aliased rather than duplicated so the two cannot drift apart.
  HeroRebranded: Hero,
  MegaMenu,
  MegaMenuDivision,
  MegaMenuV2,
  TabNavigation,
  Table,
  SimpleTable,
  ModelContractTable,
  Footer,
  SubFooterMenu,
  FooterMenu,
  SocialMediaMenu,
  LanguageSelector,
  BreadCrumbs,
  NotificationsBar,
  NotificationBox,
  Bucket,
  ButtonLineUp,
  ButtonLink,
  ShareOnSocials,
  RegisterToNewsletterForm,
  RevolutionRegisterToNewsletterForm,
  NewsletterFormIntegratedWithRevolution,
  PersonalizationCookieController,
  CustomerRating,
  ChatEntryPoint,
  DownloadContracts,
  MyEnvDisabledLandingPageCard,
  StoreButtons,
  StepExplainer,
  WarmthCheckAddressFinder,
  HeatDamageForm,
  DeceasedForm,
  WarmthInterruptions,
  DynamicPriceCardElectricityV2,
  DynamicPriceCardGasV2,
  ContractConfirmation,
  CreditWarningBar,
  PersonalizedVideo,
  // Carried by the migrated chrome but visually empty — see the component.
  GTM,
  GenericForm,
  // The 13 `GenericForm*` field blocks, spread so the form's schema builder and the renderer share
  // one source of truth (`generic-form/fields.ts`).
  ...formFieldComponents,
};

export default cmsComponents;
